/**
 * Navigation Component - Jerusalem Gold Design
 *
 * Elegant navigation with gold accents and refined interactions.
 * Bottom nav on mobile, sidebar on desktop.
 */

import { NavLink } from 'react-router-dom';
import { HomeIcon, SearchIcon, SettingsIcon, ArtistsIcon, LibraryIcon } from '../icons';

interface NavItem {
  path: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
}

const navItems: NavItem[] = [
  { path: '/', label: 'Home', icon: HomeIcon },
  { path: '/artists', label: 'Artists', icon: ArtistsIcon },
  { path: '/search', label: 'Search', icon: SearchIcon },
  { path: '/library', label: 'Library', icon: LibraryIcon },
  { path: '/settings', label: 'Settings', icon: SettingsIcon },
];

interface NavigationProps {
  variant: 'bottom' | 'sidebar';
}

export function Navigation({ variant }: NavigationProps) {
  if (variant === 'bottom') {
    return (
      <nav className="fixed bottom-0 left-0 right-0 h-16 z-40 safe-area-inset nav-bar">
        <div className="flex items-center justify-around h-full max-w-md mx-auto px-2">
          {navItems.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              className={({ isActive }) =>
                `relative flex flex-col items-center justify-center gap-1 px-5 py-2 rounded-2xl transition-all duration-200
                 ${isActive
                   ? 'text-primary'
                   : 'text-on-surface-variant hover:text-on-surface'}`
              }
            >
              {({ isActive }) => (
                <>
                  {/* Active indicator pill */}
                  {isActive && (
                    <span
                      className="absolute top-1 left-1/2 -translate-x-1/2 w-8 h-1 rounded-full bg-primary/40"
                      style={{ boxShadow: '0 0 8px rgba(212, 165, 116, 0.3)' }}
                    />
                  )}
                  <item.icon className={`w-5 h-5 transition-transform duration-200 ${isActive ? 'scale-110' : ''}`} />
                  <span className="text-2xs font-medium tracking-wide">{item.label}</span>
                </>
              )}
            </NavLink>
          ))}
        </div>
      </nav>
    );
  }

  // Sidebar variant
  return (
    <nav className="h-full flex flex-col py-6 relative">
      {/* Logo */}
      <div className="px-5 mb-8">
        <div className="flex items-center gap-3">
          <img src="./pwa-192x192.webp" alt="Zemer" className="w-10 h-10 rounded-xl" />
          <div>
            <span className="text-xl font-semibold text-on-surface tracking-tight">
              Zemer
            </span>
            <span className="block text-xs text-on-surface-variant">Music</span>
          </div>
        </div>
      </div>

      {/* Nav items */}
      <div className="flex-1 px-3 space-y-1">
        {navItems.map((item) => (
          <NavLink
            key={item.path}
            to={item.path}
            className={({ isActive }) =>
              `group relative flex items-center gap-4 px-4 py-3 rounded-xl transition-all duration-200
               ${isActive
                 ? 'bg-primary/10 text-primary'
                 : 'text-on-surface-variant hover:bg-surface-container hover:text-on-surface'}`
            }
          >
            {({ isActive }) => (
              <>
                {/* Active indicator */}
                {isActive && (
                  <span className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-6 rounded-r-full bg-primary" />
                )}
                <item.icon className={`w-5 h-5 transition-transform duration-200 ${isActive ? '' : 'group-hover:scale-105'}`} />
                <span className="font-medium">{item.label}</span>
              </>
            )}
          </NavLink>
        ))}
      </div>

      {/* Version with subtle styling */}
      <div className="px-5 py-3 border-t border-outline-variant/30">
        <p className="text-xs text-on-surface-variant/50">v0.1.0</p>
      </div>
    </nav>
  );
}
